import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredVideos = useMemo(() => {
    let result = activeSlug === 'all'
      ? [...videos]
      : videos.filter((v) => v.category === activeSlug);

    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else {
      result.sort((a, b) => b.date.localeCompare(a.date));
    }

    return result;
  }, [activeSlug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(currentCategory && activeSlug !== 'all'
                ? [{ label: '视频分类', href: '/category/all' }]
                : []),
              { label: currentCategory?.name || '全部视频' },
            ]}
          />

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
            {currentCategory?.name || '全部视频'}
            <span className="text-base font-normal text-foreground-500 ml-3">
              共 {filteredVideos.length} 部
            </span>
          </h1>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-1.5 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCategoryChange(cat.slug);
                  }}
                  className={`px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2 ml-0 sm:ml-auto">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { key: 'latest' as const, label: '最新' },
                { key: 'popular' as const, label: '最热' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 pb-14" data-product-shop="">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-foreground-500">
              <i className="ri-video-line text-5xl mb-4"></i>
              <p className="text-lg">暂无相关视频内容</p>
              <Link
                to="/category/all"
                className="mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                查看全部视频
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '全部视频'} - 91高清视频在线观看`,
            description: `${currentCategory?.name || '全部'}高清视频在线观看，超清视频在线观看不卡顿，高清影视在线畅享清晰画质。`,
            url: `https://www.example.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '91高清视频',
              url: 'https://www.example.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              ...(currentCategory && activeSlug !== 'all'
                ? [{ '@type': 'ListItem', position: 2, name: '视频分类', item: 'https://www.example.com/category/all' }]
                : []),
              {
                '@type': 'ListItem',
                position: currentCategory && activeSlug !== 'all' ? 3 : 2,
                name: currentCategory?.name || '全部视频',
              },
            ],
          }),
        }}
      />
    </>
  );
}